import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Icons } from "../assets/icon";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface Profile {
  id: string | number;
  name: string;
  type: string;
  profileImage: string;
  balance: number;
  status: string;
}

interface TalentEarningsTableProps {
  accountBalances: Profile[];
}

const TalentEarningsTable: React.FC<TalentEarningsTableProps> = ({
  accountBalances,
}) => {
  if (!accountBalances || accountBalances.length === 0) {
    return (
      <Box mt={5} textAlign="center">
        <Typography
          variant="body1"
          sx={{
            fontFamily: "'Lexend', sans-serif",
            color: COLORS.muted,
            fontSize: { xs: "0.9rem", md: "1rem" },
          }}
        >
          No talent profiles found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={5}>
      {/* Header */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: "'Lexend', sans-serif",
          fontWeight: 700,
          color: COLORS.text,
          mb: 2,
        }}
      >
        Earnings by Talent Profiles
      </Typography>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
          background: COLORS.panel,
        }}
      >
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table
            size="small"
            sx={{
              minWidth: 700,
              fontFamily: "'Lexend', sans-serif",
              "& .MuiTableCell-root": {
                borderBottom: "none",
              },
            }}
          >
            {/* Table Header */}
            <TableHead>
              <TableRow
                sx={{
                  background: "linear-gradient(120deg,#6366f1,#8b5cf6)",
                  "& th": {
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    padding: "14px",
                  },
                }}
              >
                <TableCell>S.N.</TableCell>
                <TableCell>Profile Name</TableCell>
                <TableCell>Virtual Earned Balance</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {accountBalances.map((profile, idx) => (
                <TableRow
                  key={idx}
                  component={Link}
                  to={`/wallet/${profile.id}`}
                  sx={{
                    textDecoration: "none",
                    color: "inherit",
                    backgroundColor:
                      idx % 2 === 0 ? COLORS.panel : "rgba(255,255,255,0.03)",
                    transition: "background 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  {/* Serial */}
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: COLORS.muted,
                      padding: "14px",
                    }}
                  >
                    {idx + 1}
                  </TableCell>

                  {/* Profile Name */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        component="img"
                        src={profile.profileImage}
                        alt={profile.name}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          objectFit: "cover",
                          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            color: COLORS.text,
                          }}
                        >
                          {profile.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: COLORS.muted,
                            fontSize: "0.75rem",
                          }}
                        >
                          {profile.type}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Balance */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Icons.RCoin />
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: COLORS.text,
                        }}
                      >
                        {profile.balance}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Status Badge */}
                  <TableCell>
                    <Typography
                      sx={{
                        display: "inline-block",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        color: COLORS.text,
                        backgroundColor:
                          profile.status === "Active"
                            ? "rgba(52,211,153,0.2)"
                            : profile.status === "Pending"
                              ? "rgba(245,158,11,0.2)"
                              : "rgba(239,68,68,0.2)",
                        border: `1px solid ${
                          profile.status === "Active"
                            ? "rgba(52,211,153,0.4)"
                            : profile.status === "Pending"
                              ? "rgba(245,158,11,0.4)"
                              : "rgba(239,68,68,0.4)"
                        }`,
                        px: 2,
                        py: 0.6,
                        borderRadius: "999px",
                        textTransform: "capitalize",
                      }}
                    >
                      {profile.status}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
};

export default TalentEarningsTable;
